/** Print header origin marks: 阶梯练习 / 工厂滴灌 / 品题. */

export type RevisionPrintSourceOrigin = 'pas_ladder' | 'factory_drip' | 'ttq';

const ORIGIN_LABEL: Record<RevisionPrintSourceOrigin, string> = {
  pas_ladder: '阶梯练习',
  factory_drip: '工厂滴灌',
  ttq: '品题',
};

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function isFactoryDripParticle(metadata: Record<string, unknown>): boolean {
  if (asRecord(metadata.factoryDrip)) return true;
  const workbench = asRecord(metadata.ttqWorkbench);
  if (String(workbench?.origin || '') === 'factory_ipttq_drip') return true;
  const ipttq = asRecord(metadata.ipttq);
  return String(ipttq?.origin || '') === 'factory_ipttq_drip';
}

function isPasLadderParticle(
  metadata: Record<string, unknown>,
  tags?: unknown,
): boolean {
  if (asRecord(metadata.pasPractice)) return true;
  const tagList = Array.isArray(tags)
    ? tags
    : Array.isArray(metadata.tags)
      ? metadata.tags
      : [];
  return tagList.some((tag) => String(tag) === 'pas_pkp' || String(tag) === '刻意练习');
}

function isTtqParticle(metadata: Record<string, unknown>): boolean {
  if (asRecord(metadata.ttqWorkbench)) return true;
  if (String(metadata.source || '') === 'ttq_workbench_fc') return true;
  if (asRecord(metadata.ipttq)) return true;
  if (asRecord(metadata.expansionProvenance) || asRecord(metadata.ttqSolutionDiagram)) return true;
  return false;
}

/** Prefer specific factory drip over generic TTQ workbench metadata. */
export function resolveRevisionPrintSourceOrigin(
  metadata: Record<string, unknown> | null | undefined,
  tags?: unknown,
): RevisionPrintSourceOrigin | null {
  if (!metadata && (tags === undefined || tags === null)) return null;
  const meta = metadata || {};
  if (isFactoryDripParticle(meta)) return 'factory_drip';
  if (isPasLadderParticle(meta, tags)) return 'pas_ladder';
  if (isTtqParticle(meta)) return 'ttq';
  return null;
}

function escapeAttr(value: string) {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/** Compact inline SVGs sized for A4 print (~12px). */
function originSvg(origin: RevisionPrintSourceOrigin): string {
  if (origin === 'pas_ladder') {
    // Ascending steps — aligns with PAS GrSteps mark.
    return `
      <svg class="particle-origin-svg" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d="M3.5 19.5h4.2v-3.6h4.2v-3.6h4.2V8.7h4.4" fill="none" stroke="#1d4ed8" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M3.5 19.5h17" fill="none" stroke="#93c5fd" stroke-width="1.2" stroke-linecap="round" opacity="0.85"/>
        <circle cx="19.5" cy="7.2" r="1.35" fill="#2563eb"/>
      </svg>
    `.trim();
  }
  if (origin === 'factory_drip') {
    // Teal droplet with highlight — matches FilmSeq drip badge.
    return `
      <svg class="particle-origin-svg" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d="M12 3.2C12 3.2 6.2 10.1 6.2 14.2a5.8 5.8 0 0 0 11.6 0C17.8 10.1 12 3.2 12 3.2z" fill="#14b8a6" stroke="#0f766e" stroke-width="1.15" stroke-linejoin="round"/>
        <path d="M10.1 13.4c.55-1.7 1.55-2.85 2.7-3.55" fill="none" stroke="#ecfdf5" stroke-width="1.35" stroke-linecap="round" opacity="0.95"/>
      </svg>
    `.trim();
  }
  // 品题 — utensils (same metaphor as App menu Utensils).
  return `
    <svg class="particle-origin-svg" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M8.2 3.4v7.2c0 1.15-.7 1.85-1.7 1.85S4.8 11.75 4.8 10.6V3.4" fill="none" stroke="#b45309" stroke-width="1.55" stroke-linecap="round"/>
      <path d="M6.5 12.45V20.6" fill="none" stroke="#b45309" stroke-width="1.55" stroke-linecap="round"/>
      <path d="M11.6 3.5c0 3.4 1.55 4.7 1.55 7.55V20.6" fill="none" stroke="#c2410c" stroke-width="1.55" stroke-linecap="round"/>
      <path d="M16.8 3.5v5.1c0 1.35.85 2.15 2.05 2.15h.05V20.6" fill="none" stroke="#c2410c" stroke-width="1.55" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M16.8 3.5h2.1" fill="none" stroke="#fdba74" stroke-width="1.2" stroke-linecap="round" opacity="0.9"/>
    </svg>
  `.trim();
}

export function buildRevisionPrintOriginIconMarkup(
  origin: RevisionPrintSourceOrigin | null | undefined,
): string {
  if (!origin) return '';
  const label = ORIGIN_LABEL[origin];
  return `
    <span class="particle-origin-mark particle-origin-${origin}" title="${escapeAttr(label)}" aria-label="${escapeAttr(label)}">
      ${originSvg(origin)}
    </span>
  `.trim();
}

export function buildRevisionPrintSpineRowMarkup(params: {
  sourceOrigin?: RevisionPrintSourceOrigin | null;
  spineLabel?: string | null;
  /** CSS class prefix: particle (revision) or dp (deliberate practice). */
  rowClass?: string;
  spineClass?: string;
  escapeHtml: (value: string) => string;
}): string {
  const originMarkup = buildRevisionPrintOriginIconMarkup(params.sourceOrigin);
  const spineText = String(params.spineLabel || '').trim();
  if (!originMarkup && !spineText) return '';
  const rowClass = params.rowClass || 'particle-spine-row';
  const spineClass = params.spineClass || 'particle-spine';
  return `
    <div class="${rowClass}">
      ${originMarkup}
      <div class="${spineClass}">${params.escapeHtml(spineText)}</div>
    </div>
  `.trim();
}

/** Shared CSS for origin icon + spine row (revision + DP sheets). */
export const REVISION_PRINT_ORIGIN_SPINE_CSS = `
      .particle-spine-row,
      .dp-spine-row {
        display: flex;
        align-items: center;
        gap: 5px;
        min-height: 12px;
        margin: 2px 0 6px;
        min-width: 0;
      }
      .dp-spine-row {
        margin: 0;
        flex: none;
      }
      .particle-origin-mark {
        flex: none;
        width: 13px;
        height: 13px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border-radius: 4px;
        background: #f8fafc;
        box-shadow: inset 0 0 0 1px rgba(148, 163, 184, 0.35);
      }
      .particle-origin-pas_ladder {
        background: rgba(37, 99, 235, 0.08);
        box-shadow: inset 0 0 0 1px rgba(37, 99, 235, 0.22);
      }
      .particle-origin-factory_drip {
        background: rgba(13, 148, 136, 0.09);
        box-shadow: inset 0 0 0 1px rgba(13, 148, 136, 0.24);
      }
      .particle-origin-ttq {
        background: rgba(194, 65, 12, 0.08);
        box-shadow: inset 0 0 0 1px rgba(194, 65, 12, 0.22);
      }
      .particle-origin-svg {
        width: 11px;
        height: 11px;
        display: block;
      }
      .particle-spine,
      .dp-spine {
        color: #64748b;
        font-size: 8px;
        line-height: 12px;
        font-weight: 700;
        min-height: 12px;
        min-width: 0;
        flex: 1;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .dp-spine {
        line-height: 11px;
        min-height: 11px;
      }
`;
